/**
 * Keyboard + screen-reader helpers for CCP incoming calls and active-call controls.
 * Shortcuts use Control+Shift+<key> for VoiceOver-friendly access on Mac.
 */

export const CALL_A11Y = {
    acceptShortcut: 'Control+Shift+A',
    declineShortcut: 'Control+Shift+D',
    muteShortcut: 'Control+Shift+M',
    holdShortcut: 'Control+Shift+H',
    conferenceShortcut: 'Control+Shift+C',
    keypadShortcut: 'Control+Shift+K',
    endCallShortcut: 'Control+Shift+E',
    callInfoShortcut: 'Control+Shift+I',
    acceptHint: 'Control Shift A',
    declineHint: 'Control Shift D',
    muteHint: 'Control Shift M',
    holdHint: 'Control Shift H',
    conferenceHint: 'Control Shift C',
    keypadHint: 'Control Shift K',
    endCallHint: 'Control Shift E',
    callInfoHint: 'Control Shift I',
} as const

export const CCP_FOCUS_REQUEST_EVENT = 'ccp-widget:focus-request'

export function requestCcpWidgetFocus(): void {
    window.dispatchEvent(new CustomEvent(CCP_FOCUS_REQUEST_EVENT))
}

function isModifierShortcut(event: KeyboardEvent, key: string): boolean {
    return (
        event.ctrlKey &&
        event.shiftKey &&
        !event.altKey &&
        !event.metaKey &&
        event.key.toLowerCase() === key.toLowerCase()
    )
}

export function isAcceptCallShortcut(event: KeyboardEvent): boolean {
    return isModifierShortcut(event, 'a')
}

export function isDeclineCallShortcut(event: KeyboardEvent): boolean {
    return isModifierShortcut(event, 'd')
}

export function isMuteCallShortcut(event: KeyboardEvent): boolean {
    return isModifierShortcut(event, 'm')
}

export function isHoldCallShortcut(event: KeyboardEvent): boolean {
    return isModifierShortcut(event, 'h')
}

export function isConferenceCallShortcut(event: KeyboardEvent): boolean {
    return isModifierShortcut(event, 'c')
}

export function isKeypadCallShortcut(event: KeyboardEvent): boolean {
    return isModifierShortcut(event, 'k')
}

export function isEndCallShortcut(event: KeyboardEvent): boolean {
    return isModifierShortcut(event, 'e')
}

export function isCallInfoShortcut(event: KeyboardEvent): boolean {
    return isModifierShortcut(event, 'i')
}

export function isTypingTarget(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null
    if (!element) return false
    const tag = element.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
    if (element.isContentEditable) return true
    return Boolean(element.closest('[contenteditable="true"]'))
}

export function buildIncomingCallAnnouncement(parts: {
    company?: string | null
    requester?: string | null
}): string {
    const who = [parts.company, parts.requester].filter(Boolean).join(', ')
    const from = who ? ` from ${who}` : ''
    return `Incoming call${from}. Press ${CALL_A11Y.acceptHint} to accept, or ${CALL_A11Y.declineHint} to decline.`
}

export function buildConnectingCallAnnouncement(): string {
    return 'Connecting call. Please wait.'
}

export function buildCallConnectedAnnouncement(parts: {
    company?: string | null
    requester?: string | null
}): string {
    const who = [parts.company, parts.requester].filter(Boolean).join(', ')
    if (!who) return 'Call connected. Call controls are now available.'
    return `Call connected with ${who}. Call controls are now available.`
}

export function buildMuteToggleAnnouncement(isMuted: boolean): string {
    return isMuted ? 'Microphone muted.' : 'Microphone unmuted.'
}

export function buildHoldToggleAnnouncement(isOnHold: boolean): string {
    return isOnHold ? 'All participants on hold.' : 'All participants resumed.'
}
